import moment from 'moment';
import { getFirestore } from 'firestorter';

import { firestorePaths } from '../../../../../../utils/paths';
import { BroadcastMessageRequestStatus } from '../../../../../../Model/BroadcastMessageRequest';

const scheduleSingleMessage = async (data, processedAttachments = []) => {
  const db = getFirestore();

  const messageRequestRef = db.collection(firestorePaths.CHAT_MESSAGE_REQUEST).doc();
  await messageRequestRef.set({
    ...data,
    attachments: processedAttachments,
    createdAt: moment.utc().toDate(),
    status: BroadcastMessageRequestStatus.CREATED,
  });
};

// The steps of sending feedback, as reported in its timings and failures.
const SendStep = {
  ATTACHMENTS: 'attachments',
  MESSAGE: 'message',
  ARCHIVE: 'archive',
  FEEDBACK_DOC: 'feedbackDoc',
  LAST_INTERACTION: 'lastInteraction',
};

class SendTimeoutError extends Error {
  constructor(timeoutMs) {
    super(`Sending feedback took over ${timeoutMs} ms`);
    this.name = 'SendTimeoutError';
  }
}

/**
 * Runs one step of sending feedback, recording how long it took in `progress.stepDurationsMs` and, when it fails
 * first, its name in `progress.failedStep`.
 * @param {{ stepDurationsMs: Object, failedStep: string }} progress The send's progress, updated in place
 * @param {string} step The step's name, one of `SendStep`
 * @param {Function} run The step itself
 * @returns {Promise} What the step returns
 */
const timeStep = async (progress, step, run) => {
  const startedAt = Date.now();
  try {
    return await run();
  } catch (error) {
    if (!progress.failedStep) {
      // eslint-disable-next-line no-param-reassign
      progress.failedStep = step;
    }
    throw error;
  } finally {
    // eslint-disable-next-line no-param-reassign
    progress.stepDurationsMs[step] = Date.now() - startedAt;
  }
};

/**
 * Waits for `promise`, rejecting with a `SendTimeoutError` once `timeoutMs` pass first. The promise keeps running.
 */
const withTimeout = (promise, timeoutMs) => {
  let timeoutId;
  const timeout = new Promise((resolve, reject) => {
    timeoutId = setTimeout(() => reject(new SendTimeoutError(timeoutMs)), timeoutMs);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
};

export {
  scheduleSingleMessage,
  SendStep,
  SendTimeoutError,
  timeStep,
  withTimeout,
};
